import { Suspense } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { Activity, FlaskConical, HeartPulse, Home, Menu, Plus } from 'lucide-react';
import { QuickLogProvider, useQuickLog } from './QuickLogSheet';
import { useDbState } from '../db/hooks';
import { useOptionalAccount } from '../account/AccountGate';

const tabs = [
  { to: '/', label: 'Today', icon: Home, end: true },
  { to: '/sessions', label: 'Sessions', icon: Activity },
  { to: '/health', label: 'Health', icon: HeartPulse },
  { to: '/labs', label: 'Labs', icon: FlaskConical },
  { to: '/more', label: 'More', icon: Menu }
];

function Banners() {
  const state = useDbState();
  const account = useOptionalAccount();
  if (state.status === 'ready' && state.mode === 'demo') {
    return (
      <div className="safe-top bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-amber-950">
        Demo: changes aren’t saved ·{' '}
        <a href="/" className="underline">
          Exit demo
        </a>
      </div>
    );
  }
  if (account?.entitlement.state === 'lapsed') {
    return (
      <div className="safe-top bg-red-600 px-4 py-1.5 text-center text-xs font-semibold text-white">
        Your plan has lapsed. You can view and export your data, but not add to it. ·{' '}
        <Link to="/more/account" className="underline">
          Renew
        </Link>
      </div>
    );
  }
  if (account?.entitlement.state === 'grace') {
    return (
      <div className="safe-top bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-amber-950">
        There’s a problem with your payment. Update it to keep adding entries. ·{' '}
        <Link to="/more/account" className="underline">
          Details
        </Link>
      </div>
    );
  }
  return null;
}

export function Layout() {
  return (
    <QuickLogProvider>
      <Shell />
    </QuickLogProvider>
  );
}

// Full-page forms have their own sticky save button where the floating + would sit.
const FORM_PAGE = /\/(new|edit)$/;

function Shell() {
  const quickLog = useQuickLog();
  const { pathname } = useLocation();

  return (
    <div className="min-h-dvh md:flex">
      {/* Side rail on wide screens */}
      <nav className="hidden w-56 shrink-0 border-r border-clinical-line bg-clinical-card px-3 py-6 md:block">
        <div className="mb-6 px-3 text-lg font-bold text-clinical-primary">RenalFlow</div>
        <ul className="space-y-1">
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `focus-ring flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? 'bg-clinical-primary/10 text-clinical-primary' : 'text-clinical-muted hover:text-clinical-ink'
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
        <button
          type="button"
          onClick={() => quickLog.open()}
          className="focus-ring mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-clinical-primary px-3 py-2.5 text-sm font-semibold text-white hover:bg-clinical-primary-hover"
        >
          <Plus className="h-4 w-4" /> Log
        </button>
      </nav>

      <div className="min-w-0 flex-1">
        <Banners />
        <main>
          <Suspense fallback={null}>
            <Outlet />
          </Suspense>
        </main>
      </div>

      {/* Floating log button + bottom tabs on phones */}
      {FORM_PAGE.test(pathname) ? null : (
        <button
          type="button"
          onClick={() => quickLog.open()}
          aria-label="Log something"
          className="focus-ring fixed bottom-[calc(4.5rem+env(safe-area-inset-bottom))] right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-clinical-primary text-white shadow-lg hover:bg-clinical-primary-hover md:hidden"
        >
          <Plus className="h-7 w-7" />
        </button>
      )}
      <nav className="safe-bottom fixed inset-x-0 bottom-0 z-20 border-t border-clinical-line bg-clinical-card/95 backdrop-blur md:hidden">
        <ul className="grid grid-cols-5">
          {tabs.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  `focus-ring flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium ${
                    isActive ? 'text-clinical-primary' : 'text-clinical-muted'
                  }`
                }
              >
                <Icon className="h-6 w-6" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}
